import React, {useState} from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {roomService} from '../services/roomService';
import toast from 'react-hot-toast';

const {FiPlus, FiCopy, FiCheck, FiLoader} = FiIcons;

const RoomCreation = ({onRoomCreated}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [createdRoom, setCreatedRoom] = useState(null);
  const [copied, setCopied] = useState({name: false, password: false});
  const [error, setError] = useState(null);

  const handleCreateRoom = async () => {
    setIsCreating(true);
    setError(null);
    
    try {
      const result = await roomService.createRoom();
      if (result.success) {
        setCreatedRoom(result.room);
        toast.success('Room created successfully!');
      } else {
        setError(result.error || 'Failed to create room');
        toast.error(result.error || 'Failed to create room');
      }
    } catch (error) {
      setError('Failed to create room');
      toast.error('Failed to create room');
    } finally {
      setIsCreating(false);
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied({...copied, [type]: true});
      toast.success(`${type === 'name' ? 'Room name' : 'Password'} copied!`);
      setTimeout(() => {
        setCopied({...copied, [type]: false});
      }, 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleJoinCreatedRoom = () => {
    if (createdRoom) {
      onRoomCreated(createdRoom.name, createdRoom.password);
    }
  };

  if (createdRoom) {
    return (
      <motion.div
        className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto"
        initial={{opacity: 0, scale: 0.9}}
        animate={{opacity: 1, scale: 1}}
        transition={{duration: 0.3}}
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiCheck} className="w-8 h-8 text-success-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Room Created!</h3>
          <p className="text-gray-600">Share these credentials with others to join</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Room Name
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1 p-3 bg-gray-50 rounded-lg font-mono text-sm">
                {createdRoom.name}
              </div>
              <button
                onClick={() => copyToClipboard(createdRoom.name, 'name')}
                className="p-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <SafeIcon icon={copied.name ? FiCheck : FiCopy} className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1 p-3 bg-gray-50 rounded-lg font-mono text-sm">
                {createdRoom.password}
              </div>
              <button
                onClick={() => copyToClipboard(createdRoom.password, 'password')}
                className="p-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <SafeIcon icon={copied.password ? FiCheck : FiCopy} className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          <button
            onClick={handleJoinCreatedRoom}
            className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 transition-colors"
          >
            Join This Room
          </button>
          <button
            onClick={() => setCreatedRoom(null)}
            className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors"
          >
            Create Another Room
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto"
      initial={{opacity: 0, scale: 0.9}}
      animate={{opacity: 1, scale: 1}}
      transition={{duration: 0.3}}
    >
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <SafeIcon icon={FiPlus} className="w-8 h-8 text-primary-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Create New Room</h3>
        <p className="text-gray-600">
          Generate a unique room with secure credentials
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-lg">
          <p className="text-red-600 text-sm">{error}</p>
          <p className="text-red-600 text-sm mt-1">
            Note: You can only create one room at a time. Please wait until your existing room expires (1 hour).
          </p>
        </div>
      )}

      <button
        onClick={handleCreateRoom}
        disabled={isCreating}
        className="w-full bg-primary-600 text-white py-4 px-6 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2"
      >
        {isCreating ? (
          <>
            <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
            Creating Room...
          </>
        ) : (
          <>
            <SafeIcon icon={FiPlus} className="w-5 h-5" />
            Create Room
          </>
        )}
      </button>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">What you'll get:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Unique room name</li>
          <li>• 10-character secure password</li>
          <li>• Instant room access</li>
          <li>• Real-time clipboard sharing</li>
        </ul>
      </div>
      
      <div className="mt-4 p-4 bg-amber-50 rounded-lg">
        <h4 className="font-medium text-amber-900 mb-2">Important:</h4>
        <ul className="text-sm text-amber-800 space-y-1">
          <li>• Each IP address can create only one active room at a time</li>
          <li>• Rooms automatically expire after 1 hour</li>
          <li>• All clipboard data is permanently deleted when a room expires</li>
        </ul>
      </div>
    </motion.div>
  );
};

export default RoomCreation;